import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { BRAND } from "../lib/branding";
import axios from "axios";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { Avatar, AvatarFallback } from "./ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Home,
  Target,
  Calendar,
  CalendarDays,
  Home as Cabin,
  Users,
  User,
  LogOut,
  Menu,
  X,
  Megaphone,
  ChevronDown,
  ChevronRight,
  Clock,
  Crosshair,
  MessageSquare,
  Map,
  Camera,
  History,
  FolderOpen,
  MessageCircle,
  Archive,
  Mail,
  BarChart3,
  Settings,
  Send,
  TreePine,
  Shield,
  Tag,
  GraduationCap,
  Download,
  CreditCard,
  HelpCircle,
  Sparkles,
  PartyPopper,
  Upload
} from "lucide-react";
import PushNotificationToggle from "./PushNotificationToggle";
import AnnouncementPopup from "./AnnouncementPopup";
import NewsSummaryDialog from "./NewsSummaryDialog";
import InstallPWAButton from "./InstallPWAButton";
import PWAInstallBanner from "./PWAInstallBanner";
import VeziceFAB from "./VeziceFAB";
import ImpersonationBanner, { ImpersonationBadge } from "./ImpersonationBanner";
import SAImpersonationOverlay from "./SAImpersonationOverlay";
import ScreenMirrorPrompt from "./ScreenMirrorPrompt";
import DemoBanner from "./DemoBanner";
import { DevicePreviewFAB } from "./DevicePreview";
import { toast } from "sonner";

const Layout = () => {
  const { user, logout, hasRole, token } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [appVersion, setAppVersion] = useState("1.3");
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [unreadCounts, setUnreadCounts] = useState({ obvestila: 0, dogodki: 0, forum: 0 });
  const [hasTrainingAccess, setHasTrainingAccess] = useState(false);
  const [hasCameraAccess, setHasCameraAccess] = useState(false);
  const [hasAiJawAccess, setHasAiJawAccess] = useState(false);
  const [pendingLds, setPendingLds] = useState(0);

  // Fetch version and unread count on mount
  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const response = await axios.get(`${API}/version`);
        setAppVersion(response.data.version);
      } catch (error) {
        console.error("Error fetching version:", error);
      }
    };
    const fetchUnread = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API}/sporocila/neprebrana`, { headers });
        const n = res.data.neprebrana || 0;
        setUnreadMessages(n);
        // Sync badge to service worker (for app icon)
        try {
          if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({ type: "set-badge", count: n });
          }
          if ("setAppBadge" in navigator) {
            if (n > 0) navigator.setAppBadge(n).catch(() => { /* not supported */ });
            else navigator.clearAppBadge?.().catch(() => { /* not supported */ });
          }
        } catch (_) { /* silent */ }
      } catch (e) { /* silent - non-critical background fetch */ }
    };
    const fetchUnreadCategories = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API}/me/notification-prefs/unread-counts`, { headers });
        setUnreadCounts(res.data?.counts || { obvestila: 0, dogodki: 0, forum: 0 });
      } catch (e) { /* silent */ }
    };
    const checkTrainingAccess = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API}/training/pripravniki`, { headers });
        setHasTrainingAccess((res.data || []).length > 0);
      } catch (e) { setHasTrainingAccess(false); }
    };
    const fetchPendingLds = async () => {
      if (user?.vloga !== "super_admin") return;
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API}/superadmin/lovske-druzine`, { headers });
        const count = (res.data || []).filter(l => l.status === "pending").length;
        setPendingLds(count);
      } catch (e) { /* silent */ }
    };
    const checkCameraAccess = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API}/cameras/access-check`, { headers });
        setHasCameraAccess(!!res.data?.has_access);
      } catch (e) { setHasCameraAccess(false); }
    };
    const checkAiJawAccess = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API}/ai-jaw/access-check`, { headers });
        setHasAiJawAccess(!!res.data?.has_access);
      } catch (e) { setHasAiJawAccess(false); }
    };
    fetchVersion();
    fetchUnread();
    fetchUnreadCategories();
    checkTrainingAccess();
    checkCameraAccess();
    checkAiJawAccess();
    fetchPendingLds();
    const interval = setInterval(fetchUnread, 15000);
    const categoriesInterval = setInterval(fetchUnreadCategories, 30000);
    const pendingInterval = setInterval(fetchPendingLds, 60000);
    
    // Foreground push messages – Firebase onMessage. Re-fetch unread count and show toast.
    let unsub = null;
    (async () => {
      try {
        const { initializeMessaging } = await import("../lib/firebase");
        const { onMessage } = await import("firebase/messaging");
        const messaging = await initializeMessaging();
        if (messaging && Notification?.permission === "granted") {
          unsub = onMessage(messaging, (payload) => {
            const t = payload.notification?.title || "Novo obvestilo";
            const b = payload.notification?.body || "";
            toast(t, { description: b, duration: 6000 });
            fetchUnread();
          });
        }
      } catch (_) { /* silent - firebase not initialized */ }
    })();
    
    return () => { clearInterval(interval); clearInterval(categoriesInterval); clearInterval(pendingInterval); if (unsub) unsub(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Navigation - flat items + grouped submenus only for admin sections
  const navGroups = [
    { path: "/dashboard", icon: Home, label: "Nadzorna plošča", roles: null },
    { path: "/plan-odstrela", icon: Target, label: "Plan odstrela", roles: null },
    { path: "/karta", icon: Map, label: "Karta lovišča", roles: null },
    { path: "/kamere", icon: Camera, label: "Kamere", customVisible: hasCameraAccess },
    { path: "/ai-ocena", icon: Sparkles, label: "AI ocena starosti", customVisible: hasAiJawAccess },
    { path: "/koledar", icon: CalendarDays, label: "Koledar", roles: null },
    { path: "/dogodki", icon: Calendar, label: "Dogodki", roles: null, unreadKey: "dogodki" },
    { path: "/koce", icon: Cabin, label: "Koče", roles: null },
    { path: "/sporocila", icon: Mail, label: "Sporočila", roles: null, badge: true },
    { path: "/obvestila", icon: Megaphone, label: "Obvestila", roles: null, unreadKey: "obvestila" },
    { path: "/clani", icon: Users, label: "Člani", roles: null },
    { path: "/ure", icon: Clock, label: "Delovne ure", roles: ["predsednik", "gospodar", "upravnik_lovske_koce", "skupinovodja"] },
    {
      path: "/izobrazevanje",
      icon: GraduationCap,
      label: "Plan izobraževanja",
      customVisible: hasRole(["predsednik", "gospodar", "komisija_izobrazevanje", "pripravnik"]) || hasTrainingAccess
    },
    { path: "/orozje", icon: Crosshair, label: "Moje orožje", roles: null, hidden: true },
    { path: "/profil?tab=odstrel", icon: Target, label: "Moj odstrel", roles: null, hidden: true },
    {
      id: "strelstvo", icon: Crosshair, label: "Strelstvo", roles: null,
      children: [
        { path: "/profil?tab=odstrel", icon: Target, label: "Moj odstrel", roles: null },
        { path: "/orozje", icon: Crosshair, label: "Moje orožje", roles: null },
        { path: "/vezice", icon: Tag, label: "Vezice", roles: null },
        { path: "/zgreseni-streli", icon: Crosshair, label: "Prijava zgrešenega strela", roles: null },
      ]
    },
    { path: "/zgreseni-streli", icon: Crosshair, label: "Prijava zgrešenega strela", roles: null, hidden: true },
    { path: "/datoteke", icon: FolderOpen, label: "Datoteke", roles: null },
    {
      id: "posiljanje", icon: Send, label: "Pošiljanje", roles: ["predsednik", "gospodar", "tajnik", "blagajnik", "skupinovodja"],
      children: [
        { path: "/sms", icon: MessageSquare, label: "Pošlji SMS", roles: ["predsednik", "gospodar", "tajnik", "blagajnik", "skupinovodja"] },
        { path: "/poslji-email", icon: Mail, label: "Pošlji e-mail", roles: ["predsednik", "gospodar", "tajnik"] },
      ]
    },
    {
      id: "upravljanje", icon: Shield, label: "Upravljanje", roles: ["predsednik", "tajnik", "gospodar"],
      children: [
        { path: "/vloge", icon: Shield, label: "Dodeljevanje vlog", roles: ["predsednik"] },
        { path: "/skupine", icon: Shield, label: "Skupine in revirji", roles: ["predsednik", "tajnik", "gospodar"] },
        { path: "/lisjak", icon: Shield, label: "Lisjak sinhronizacija", roles: ["predsednik", "tajnik", "gospodar"] },
        { path: "/izvozi", icon: Download, label: "Izvozi", roles: ["predsednik", "tajnik", "gospodar", "blagajnik", "upravnik_lovske_koce"] },
        { path: "/statistike", icon: BarChart3, label: "Statistike", roles: ["predsednik", "tajnik", "gospodar"] },
        { path: "/jubileji", icon: PartyPopper, label: "Koledar jubilejev", roles: ["predsednik", "tajnik", "gospodar"] },
        { path: "/karta?uvoz=1", icon: Upload, label: "Uvoz na karto", roles: ["predsednik", "gospodar"] },
        { path: "/arhiv-prisotnosti", icon: Archive, label: "Arhiv prisotnosti", roles: ["predsednik", "gospodar", "tajnik"] },
        { path: "/email-nastavitve", icon: Settings, label: "E-mail nastavitve", roles: ["predsednik", "tajnik"] },
        { path: "/dnevnik", icon: History, label: "Dnevnik dogodkov", roles: ["predsednik", "tajnik", "gospodar", "blagajnik"] },
        { path: "/dostopi", icon: History, label: "Beleženje dostopov", roles: ["predsednik", "tajnik"] },
      ]
    },
    { path: "/profil", icon: User, label: "Moj profil", roles: null },
    ...(BRAND.subscriptionUiHidden
      ? []
      : [{ path: "/narocnina", icon: CreditCard, label: "Naročnina", roles: ["predsednik", "administrator", "gospodar", "blagajnik", "tajnik"] }]
    ),
    { path: "/super-admin", icon: Shield, label: "Super-admin", roles: ["super_admin"], superAdminBadge: true },
  ];

  const [expandedGroups, setExpandedGroups] = useState({});

  const toggleGroup = (id) => {
    setExpandedGroups(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Filter groups and children by role.
  // SPECIAL: super_admin sees ONLY their super-admin menu (+ profile + logout). No LD-scoped items.
  const isSuperAdmin = user?.vloga === "super_admin";
  const filteredGroups = navGroups.filter(group => {
    if (group.hidden) return false;
    if (isSuperAdmin) {
      // Allow only: super-admin route and Moj profil
      if (group.path === "/super-admin") return true;
      if (group.path === "/profil") return true;
      return false;
    }
    if (group.customVisible !== undefined) {
      return group.customVisible;
    }
    if (group.children) {
      const visibleChildren = group.children.filter(c => !c.roles || hasRole(c.roles));
      return visibleChildren.length > 0;
    }
    return !group.roles || hasRole(group.roles);
  });

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const getRoleLabel = (role) => {
    const labels = {
      administrator: "Administrator",
      predsednik: "Predsednik",
      gospodar: "Gospodar",
      blagajnik: "Blagajnik",
      tajnik: "Tajnik",
      upravnik_lovske_koce: "Upravnik lovske koče",
      skupinovodja: "Skupinovodja",
      super_admin: "Super-admin",
      kinoloski_referent: "Kinološki referent",
      referent_strelstvo: "Referent za strelstvo",
      komisija_skode: "Komisija za škode",
      komisija_izobrazevanje: "Komisija za izobraževanje",
      komisija_trofeje: "Komisija za ocenjevanje trofej",
      disciplinska_komisija: "Disciplinska komisija",
      pripravnik: "Pripravnik",
      clan: "Član"
    };
    return labels[role] || role;
  };

  return (
    <div className="min-h-screen bg-secondary/30">
      <SAImpersonationOverlay />
      <ImpersonationBanner />
      <DemoBanner />
      {/* Mobile Header - higher for notch phones */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-20 bg-white border-b z-50 flex items-end pb-2 px-4 pt-6">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="mobile-menu-btn"
          className="h-12 w-12 min-h-12 min-w-12"
        >
          {sidebarOpen ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
        </Button>
        <div className="flex items-center gap-2 ml-3 flex-1 min-w-0">
          <img
            src={BRAND.logoPath}
            alt={BRAND.brandName}
            className="h-9 w-9 object-contain rounded-full flex-shrink-0"
          />
          <div className="flex flex-col min-w-0">
            <span className="font-semibold text-primary text-base leading-tight truncate" style={{ fontFamily: "'Playfair Display', serif" }}>{BRAND.brandName}</span>
            {user?.ld_naziv && (
              <div className="text-[10px] text-muted-foreground truncate leading-tight flex items-center gap-1">
                <span>{user.ld_naziv}</span>
                <ImpersonationBadge />
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 mr-2 leading-tight">
          <a
            href="/navodila.html"
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 rounded-lg hover:bg-primary/10 text-primary"
            data-testid="mobile-help-link"
            title="Navodila za uporabo"
          >
            <HelpCircle className="h-5 w-5" />
          </a>
          <span className="text-[9px] text-muted-foreground text-right">Verzija {appVersion}</span>
        </div>
      </header>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 sidebar text-white z-40
        transform transition-transform duration-300 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 flex items-center gap-3 border-b border-white/10">
            <div className="w-12 h-12 rounded-full bg-white overflow-hidden flex items-center justify-center ring-2 ring-white/20">
              <img
                src={BRAND.logoPath}
                alt={BRAND.brandName}
                className="w-11 h-11 object-contain"
              />
            </div>
            <div className="min-w-0">
              <h1 className="font-serif font-bold text-lg leading-tight">{BRAND.brandName}</h1>
              {user?.ld_naziv ? (
                <div className="text-xs text-white/70 truncate leading-tight flex items-center gap-1">
                  <span className="truncate">{user.ld_naziv}</span>
                  <ImpersonationBadge />
                </div>
              ) : (
                <p className="text-xs text-white/60">{BRAND.tagline}</p>
              )}
              <p className="text-[8px] text-white/40 mt-0.5">Verzija {appVersion}</p>
            </div>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-0.5">
              {filteredGroups.map((group) => {
                if (group.children) {
                  const visibleChildren = group.children.filter(c => !c.roles || hasRole(c.roles));
                  const isExpanded = expandedGroups[group.id];
                  return (
                    <div key={group.id}>
                      <button
                        onClick={() => toggleGroup(group.id)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200
                          ${isExpanded ? 'bg-white/10 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'}
                        `}
                        data-testid={`nav-group-${group.id}`}
                      >
                        <group.icon className="h-5 w-5" />
                        <span className="flex-1 text-left text-sm">{group.label}</span>
                        {group.id === "komunikacija" && unreadMessages > 0 && (
                          <span className="bg-red-500 text-white text-[10px] font-bold rounded-full h-4 min-w-4 flex items-center justify-center px-1 mr-1">
                            {unreadMessages}
                          </span>
                        )}
                        <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} />
                      </button>
                      {isExpanded && (
                        <div className="ml-4 mt-0.5 space-y-0.5 border-l border-white/10 pl-2">
                          {visibleChildren.map((child) => (
                            <NavLink
                              key={child.path}
                              to={child.path}
                              onClick={() => setSidebarOpen(false)}
                              className={({ isActive }) => `
                                flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-sm
                                ${isActive 
                                  ? 'bg-white/20 text-white font-medium' 
                                  : 'text-white/60 hover:bg-white/10 hover:text-white'
                                }
                              `}
                              data-testid={`nav-${child.path.replace('/', '')}`}
                            >
                              <child.icon className="h-4 w-4" />
                              <span className="flex-1">{child.label}</span>
                              {child.badge && unreadMessages > 0 && (
                                <span className="bg-red-500 text-white text-[10px] font-bold rounded-full h-4 min-w-4 flex items-center justify-center px-1">
                                  {unreadMessages}
                                </span>
                              )}
                            </NavLink>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }
                // Simple nav item (no children)
                return (
                  <NavLink
                    key={group.path}
                    to={group.path}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `
                      flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200
                      ${isActive 
                        ? 'bg-white/20 text-white font-medium' 
                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                      }
                    `}
                    data-testid={`nav-${group.path.replace('/', '')}`}
                  >
                    <group.icon className="h-5 w-5" />
                    <span className="flex-1 text-sm">{group.label}</span>
                    {group.badge && unreadMessages > 0 && (
                      <span className="bg-red-500 text-white text-[10px] font-bold rounded-full h-4 min-w-4 flex items-center justify-center px-1">
                        {unreadMessages}
                      </span>
                    )}
                    {group.unreadKey && (unreadCounts[group.unreadKey] || 0) > 0 && (
                      <span
                        className="bg-red-500 text-white text-[10px] font-bold rounded-full h-4 min-w-4 flex items-center justify-center px-1"
                        data-testid={`nav-unread-${group.unreadKey}`}
                      >
                        {unreadCounts[group.unreadKey]}
                      </span>
                    )}
                    {group.superAdminBadge && pendingLds > 0 && (
                      <span
                        className="bg-amber-500 text-white text-[10px] font-bold rounded-full h-4 min-w-4 flex items-center justify-center px-1 animate-pulse"
                        data-testid="nav-super-admin-pending-badge"
                        title={`${pendingLds} čakajočih registracij LD`}
                      >
                        {pendingLds}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </nav>
          </ScrollArea>

          {/* Push notification toggle */}
          <div className="px-4 py-2 border-t border-white/10">
            <PushNotificationToggle />
          </div>

          {/* User section */}
          <div className="p-4 border-t border-white/10">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white/10 transition-colors" data-testid="user-menu-btn">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-white/20 text-white">
                      {user?.ime?.[0]}{user?.priimek?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium">{user?.ime} {user?.priimek}</p>
                    <p className="text-xs text-white/60">{getRoleLabel(user?.vloga)}</p>
                  </div>
                  <ChevronDown className="h-4 w-4 text-white/60" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Moj račun</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { navigate("/profil"); setSidebarOpen(false); }} data-testid="profile-menu-item">
                  <User className="mr-2 h-4 w-4" />
                  Profil
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="help-menu-item">
                  <a href="/navodila.html" target="_blank" rel="noopener noreferrer" className="cursor-pointer">
                    <HelpCircle className="mr-2 h-4 w-4" />
                    Navodila za uporabo
                  </a>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <div className="px-1 py-1">
                  <InstallPWAButton asMenuItem />
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-menu-item">
                  <LogOut className="mr-2 h-4 w-4" />
                  Odjava
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Bottom brand attribution */}
          <div className="px-4 pb-3 pt-1 border-t border-white/10 text-center leading-tight" data-testid="sidebar-brand-footer">
            <p className="text-[9px] text-white/50">Sistem poganja</p>
            <p className="text-[10px] text-white/70 font-medium">Medvedji brlog</p>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="lg:ml-64 pt-20 lg:pt-0 min-h-screen">
        <PWAInstallBanner />
        <ScreenMirrorPrompt token={token} user={user} />
        <div className="p-4 md:p-6 lg:p-8">
          <Outlet />
        </div>
        {/* Bottom brand attribution (mobile & desktop content area) */}
        <footer
          className="lg:hidden text-center py-4 border-t bg-background/50 leading-tight"
          data-testid="mobile-brand-footer"
        >
          <p className="text-[10px] text-muted-foreground">Sistem poganja</p>
          <p className="text-xs text-primary font-medium">Medvedji brlog</p>
        </footer>
      </main>

      {/* Announcement popup / novice / Vezice – ne za super-admina */}
      {!isSuperAdmin && <AnnouncementPopup />}

      {/* News summary on login */}
      {!isSuperAdmin && <NewsSummaryDialog />}

      {/* Floating Vezice button (config-driven) */}
      {!isSuperAdmin && <VeziceFAB />}

      {/* Device preview FAB – samo za demo uporabnike (ne v ugnezdenem iframe-u) */}
      {user?.is_demo && typeof window !== "undefined" && window.self === window.top && (
        <DevicePreviewFAB
          src={`${window.location.pathname}${window.location.search}${window.location.search ? "&" : "?"}share_auth=1`}
        />
      )}
    </div>
  );
};

export default Layout;
